import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Drawer, Input, Popconfirm, Select, Space, Switch, Table, Tabs, Tooltip, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import { useSearchParams } from 'react-router-dom';

import TrashIcon from 'assets/icons/trash.svg';
import PenIcon from 'assets/icons/pen.svg';
import { deleteDistrict, listDistrictsByActive, listDistrictsByState, setDistrictActive } from '../services/districtService';
import type { District } from '../types/District';
import DistrictForm from './Form';
import { extractApiErrorMessage, openNotification } from '../../../utils';
import { listStatesByActive } from '../../states/services/stateService';
import type { State } from '../../states/types/State';

type StatusTabKey = 'active' | 'passive';
const DEFAULT_PAGE_SIZE = 10;

const parseOptionalId = (value: string | null): number | undefined => {
  if (!value?.trim()) {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const dedupeStates = (items: State[]) => {
  const map = new Map<number, State>();
  items.forEach((item) => {
    if (typeof item?.id === 'number') {
      map.set(item.id, item);
    }
  });
  return Array.from(map.values()).sort((a, b) => String(a?.name ?? '').localeCompare(String(b?.name ?? ''), 'tr'));
};

export default function DistrictList({ isActive = true }: { isActive?: boolean }) {
  const { Text } = Typography;
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = searchParams.get('active') === 'false' ? 'passive' : 'active';
  const initialStateId = parseOptionalId(searchParams.get('stateId'));

  const [data, setData] = useState<District[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | undefined>();
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusTab, setStatusTab] = useState<StatusTabKey>(initialStatus);
  const [selectedStateId, setSelectedStateId] = useState<number | undefined>(initialStateId);
  const [pendingStateId, setPendingStateId] = useState<number | undefined>(initialStateId);
  const [statusUpdatingId, setStatusUpdatingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [stateOptions, setStateOptions] = useState<State[]>([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: DEFAULT_PAGE_SIZE });

  const isActiveFilter = statusTab === 'active';
  const debouncedApplySearch = useMemo(() => debounce((value: string) => setSearch(value), 300), []);
  const stateNameById = useMemo(() => {
    const map = new Map<number, string>();
    stateOptions.forEach((state) => {
      if (typeof state?.id === 'number') {
        map.set(state.id, String(state?.name ?? state.id));
      }
    });
    return map;
  }, [stateOptions]);

  useEffect(() => () => debouncedApplySearch.cancel(), [debouncedApplySearch]);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    Promise.all([listStatesByActive(true), listStatesByActive(false)])
      .then(([activeItems, passiveItems]) => {
        setStateOptions(dedupeStates([...(activeItems || []), ...(passiveItems || [])]));
      })
      .catch((error) => {
        const message = extractApiErrorMessage(error, 'İl filtreleri yüklenemedi');
        openNotification('error', 'Hata', message);
        setStateOptions([]);
      });
  }, [isActive]);

  const syncSearchParams = useCallback(
    (nextStatus: StatusTabKey, nextStateId?: number) => {
      const next = new URLSearchParams(searchParams);
      next.set('active', String(nextStatus === 'active'));
      if (typeof nextStateId === 'number') {
        next.set('stateId', String(nextStateId));
      } else {
        next.delete('stateId');
      }
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const fetchData = useCallback(async () => {
    if (!isActive) {
      return;
    }

    try {
      setLoading(true);
      const items =
        typeof selectedStateId === 'number'
          ? await listDistrictsByState(selectedStateId, isActiveFilter)
          : await listDistrictsByActive(isActiveFilter);
      setData(Array.isArray(items) ? items : []);
    } catch (error) {
      const message = extractApiErrorMessage(error, 'İlçeler yüklenemedi');
      openNotification('error', 'Hata', message);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [isActive, isActiveFilter, selectedStateId]);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    fetchData();
  }, [fetchData, isActive]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [statusTab, search, selectedStateId]);

  const filteredData = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase('tr-TR');
    if (normalizedSearch.length < 2) {
      return data;
    }

    return data.filter((item) => {
      const stateName = typeof item?.stateId === 'number' ? stateNameById.get(item.stateId) : '';
      return [item.id, item.name, item.type, item.stateId, stateName].some((value) =>
        String(value ?? '').toLocaleLowerCase('tr-TR').includes(normalizedSearch),
      );
    });
  }, [data, search, stateNameById]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredData.length / pagination.pageSize));
    if (pagination.current > maxPage) {
      setPagination((prev) => ({ ...prev, current: maxPage }));
    }
  }, [filteredData.length, pagination.current, pagination.pageSize]);

  const handleDelete = async (id?: number) => {
    if (typeof id !== 'number') return;
    try {
      setDeletingId(id);
      await deleteDistrict(id);
      setData((prev) => prev.filter((item) => item.id !== id));
      openNotification('success', 'Başarılı', 'İlçe silindi');
    } catch (error) {
      const message = extractApiErrorMessage(error, 'İlçe silinemedi');
      openNotification('error', 'Hata', message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleStatusToggle = async (record: District, nextValue: boolean) => {
    if (typeof record?.id !== 'number') return;
    try {
      setStatusUpdatingId(record.id);
      await setDistrictActive(record.id, nextValue);
      setData((prev) =>
        prev.filter((item) => {
          if (item.id !== record.id) return true;
          return nextValue === isActiveFilter;
        }),
      );
      openNotification('success', 'Başarılı', nextValue ? 'İlçe aktif edildi' : 'İlçe pasif edildi');
    } catch (error) {
      const message = extractApiErrorMessage(error, 'Durum güncellenemedi');
      openNotification('error', 'Hata', message);
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      sorter: (a: District, b: District) => Number(a?.id ?? 0) - Number(b?.id ?? 0),
      width: 90,
    },
    {
      title: 'İlçe',
      dataIndex: 'name',
      sorter: (a: District, b: District) => String(a?.name ?? '').localeCompare(String(b?.name ?? ''), 'tr'),
    },
    {
      title: 'İl',
      dataIndex: 'stateId',
      width: 220,
      render: (stateId: number) => stateNameById.get(stateId) ?? stateId ?? '-',
      sorter: (a: District, b: District) =>
        String(stateNameById.get(Number(a?.stateId)) ?? '').localeCompare(
          String(stateNameById.get(Number(b?.stateId)) ?? ''),
          'tr-TR',
          { sensitivity: 'base' },
        ),
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      width: 140,
      render: (value: boolean, record: District) => (
        <Switch
          checked={!!value}
          checkedChildren='Aktif'
          unCheckedChildren='Pasif'
          onChange={(checked) => handleStatusToggle(record, checked)}
          loading={statusUpdatingId === record.id}
        />
      ),
    },
    {
      title: 'İşlemler',
      width: 180,
      render: (_: unknown, record: District) => (
        <Space>
          <Tooltip title='Düzenle'>
            <Button
              icon={<img src={PenIcon} alt='pen' className='icon' />}
              shape='circle'
              className='icon-button'
              onClick={() => {
                setEditingId(record.id);
                setOpen(true);
              }}
            />
          </Tooltip>
          <Popconfirm title='Sil?' onConfirm={() => handleDelete(record.id)}>
            <Tooltip title='Sil'>
              <Button
                icon={<img src={TrashIcon} alt='trash' className='icon' />}
                shape='circle'
                className='icon-button ml-12px'
                loading={deletingId === record.id}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        className='br-30px kontrolx-card'
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Tabs
              activeKey={statusTab}
              onChange={(key) => {
                const nextStatus: StatusTabKey = key === 'passive' ? 'passive' : 'active';
                setStatusTab(nextStatus);
                syncSearchParams(nextStatus, selectedStateId);
              }}
              className='custom-tabs'
            >
              <Tabs.TabPane
                key='active'
                tab={<Button type={statusTab === 'active' ? 'primary' : 'default'} className='d-button tab-button'>Aktif İlçe Listesi</Button>}
              />
              <Tabs.TabPane
                key='passive'
                tab={<Button type={statusTab === 'passive' ? 'primary' : 'default'} className='d-button tab-button'>Pasif İlçe Listesi</Button>}
              />
            </Tabs>
            <Text type='secondary' style={{ fontWeight: 500, marginTop: 24 }}>
              <strong>({filteredData.length})</strong> kayıt listelendi
            </Text>
          </div>
        }
        extra={
          <Space>
            <Input
              allowClear
              placeholder='Ara'
              prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
              className='d-input d-input-form d-input-filter'
              style={{ width: 220 }}
              value={searchInput}
              onChange={(event) => {
                const value = event.target.value || '';
                setSearchInput(value);
                if (value.length >= 2) {
                  debouncedApplySearch(value);
                } else if (!value.length) {
                  debouncedApplySearch('');
                }
              }}
            />
            <Button type='primary' onClick={() => setFilterOpen(true)}>
              Filtrele
            </Button>
            <Button type='primary' onClick={() => { setEditingId(undefined); setOpen(true); }}>
              Ekle
            </Button>
          </Space>
        }
      >
        <Table
          rowKey='id'
          dataSource={filteredData}
          columns={columns}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt`,
          }}
          onChange={(nextPagination) => {
            setPagination({
              current: nextPagination.current ?? 1,
              pageSize: nextPagination.pageSize ?? DEFAULT_PAGE_SIZE,
            });
          }}
        />
      </Card>
      <Drawer open={filterOpen} onClose={() => setFilterOpen(false)} destroyOnClose width={420} title='Filtre'>
        <Space direction='vertical' style={{ width: '100%' }} size='large'>
          <div>
            <Typography.Text>İl</Typography.Text>
            <Select
              allowClear
              showSearch
              placeholder='İl seçiniz'
              style={{ width: '100%', marginTop: 8 }}
              value={pendingStateId}
              filterOption={(input, option) =>
                String(option?.children ?? '').toLocaleLowerCase('tr-TR').includes(input.toLocaleLowerCase('tr-TR'))
              }
              onChange={(value) => setPendingStateId(typeof value === 'number' ? value : undefined)}
            >
              {stateOptions.map((state) => (
                <Select.Option key={state.id} value={state.id}>
                  {state.name}
                </Select.Option>
              ))}
            </Select>
          </div>
          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <Button
              onClick={() => {
                setPendingStateId(undefined);
                setSelectedStateId(undefined);
                syncSearchParams(statusTab);
                setFilterOpen(false);
              }}
            >
              Temizle
            </Button>
            <Button
              type='primary'
              onClick={() => {
                setSelectedStateId(pendingStateId);
                syncSearchParams(statusTab, pendingStateId);
                setFilterOpen(false);
              }}
            >
              Filtrele
            </Button>
          </Space>
        </Space>
      </Drawer>
      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        destroyOnClose
        width={520}
        title={editingId ? 'İlçe Düzenle' : 'İlçe Ekle'}
      >
        <DistrictForm
          id={editingId}
          onSuccess={() => {
            setOpen(false);
            fetchData();
          }}
        />
      </Drawer>
    </>
  );
}
